import type { MouseEvent } from "react";
import { useContext, useState } from "react";
import { useTranslation } from "../../i18n";
import type { Job } from "../../types/job";
import AudioPlayer from "../AudioPlayer";
import { isQuestionBatchType, getQuestionBatchInfo } from "../../lib/questionBatch";
import { withMwRoot } from "../../lib/mwRoot";
import { formatModel, hasScene, jobDisplayName } from "../../lib/jobDisplay";
import { HostBridgeContext } from "../../hostBridge";

export default function JobImageCard({
  job,
  statusLabel,
  onClick,
  showHistory = false,
  onApproveClick,
  onDeclineClick,
  onDismissClick,
  approveLabel,
  declineLabel,
}: {
  job: Job;
  statusLabel?: string;
  onClick: () => void;
  showHistory?: boolean;
  onApproveClick?: () => void;
  onDeclineClick?: () => void;
  onDismissClick?: () => void;
  approveLabel?: string;
  declineLabel?: string;
}) {
  const { t, i18n } = useTranslation();
  const { onAddToScene } = useContext(HostBridgeContext);
  const [isApproving, setIsApproving] = useState(false);

  const modelLabel = formatModel(job.model, t);

  const getStatusBadge = () => {
    if (showHistory) {
      if (job.deletedAt) {
        return (
          <span className="text-[10px] text-red-600 font-semibold">{t("status.declined")}</span>
        );
      }
      if (job.approved) {
        return (
          <span className="text-[10px] text-green-600 font-semibold">{t("status.approved")}</span>
        );
      }
      return <span className="text-[10px] text-gray-500 font-semibold">{t("status.pending")}</span>;
    }

    const normalized = (statusLabel || job.status || "").toLowerCase();

    const tone = normalized.includes("process")
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : normalized.includes("approval") || normalized.includes("inspect")
        ? "bg-purple-50 text-purple-700 border-purple-200"
        : normalized.includes("approv")
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : normalized.includes("declin")
            ? "bg-red-50 text-red-600 border-red-200"
            : "bg-gray-50 text-gray-700 border-gray-200";

    return (
      <span
        className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${tone} whitespace-nowrap`}
      >
        {statusLabel || job.status}
      </span>
    );
  };

  const createdLabel = job.createdAt
    ? new Date(job.createdAt).toLocaleDateString(i18n.language === "tr" ? "tr-TR" : "en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  // Sahne anlatımı (training-narration): kartta önizleme/ses gösterme, sadece detayda
  const isNarration =
    (job.type || "")
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-") === "training-narration";
  // Soru oluşturma (question-batch-generate): sahne yerine ilerleme/üretilen soru sayısı göster
  const isQuestionBatch = isQuestionBatchType(job);
  const qb = isQuestionBatch ? getQuestionBatchInfo(job) : null;
  const isVideoGeneration = job.type === "video-generation" || job.type === "video_generation";
  // Doğal konuşan video: approve replaces the scene narration + adds the video
  // muted (host side) — never the plain "add video" shortcut below.
  const isTalking = job.type === "talking-avatar" || job.type === "talking_avatar";
  const isAvatarType = [
    "ai-avatar",
    "ai_avatar",
    "tavus-video",
    "tts-avatar",
    "lipsync",
    "image-to-avatar",
    "image_to_avatar_generation",
    "talking-avatar",
    "talking_avatar",
  ].includes(job.type);
  const talkingRunning =
    isTalking &&
    !showHistory &&
    !["inspect", "completed", "failed", "approved", "declined"].includes(
      String(job.status || "").toLowerCase()
    );
  // Like the Avatar tab: one percentage, no pipeline stages.
  const talkingProgress = Math.round(Math.min(100, Math.max(0, Number(job.progress) || 0)));

  // Motion Director (Sync with AI → Enrich & Motionize) applies itself: while it runs, its step;
  // once applied, what it built (mw writes metadata.motionSummary; the job row has no result).
  const isMotion = job.type === "motion-director";
  const motionStep =
    isMotion && !showHistory && !job.approved
      ? job.stage === "extracting" && job.segment
        ? t("jobCard.motionExtracting", job.segment)
        : job.stage === "designing"
          ? t("jobCard.motionDesigning")
          : null
      : null;
  const motionSummary = isMotion && job.approved ? (job.metadata as any)?.motionSummary : null;
  const motionApplied = motionSummary ? t("jobCard.motionApplied", motionSummary) : null;

  const metadata = (job.metadata ?? {}) as {
    videoUrl?: string;
    avatarImageUrl?: string;
    audioUrl?: string;
  };

  // Collect preview media (image or video) depending on job type (same logic as VideoGenerationModal)
  const videoData = job.result || (job as any).data || {};
  let primaryVideoUrl =
    videoData.finalVideoUrl ||
    videoData.videoUrl ||
    videoData.url ||
    metadata.videoUrl ||
    job.resultUrl ||
    "";

  // If outputPath exists, construct full URL
  if (!primaryVideoUrl && videoData.fileVirtualPath) {
    primaryVideoUrl = videoData.fileVirtualPath;
  }

  const isAudioFile = (u?: string | null) => !!u && /\.(mp3|wav|m4a|aac|oga)(\?|#|$)/i.test(u);

  const previewMedia: { url: string; kind: "video" | "image" | "audio" }[] = [];

  // Avatar / lipsync: before image + generated video
  if (["ai-avatar", "ai_avatar", "lipsync"].includes(job.type) || isTalking) {
    // ai-avatar / lipsync: kaynak/önceki görseli gösterme — sadece üretilen video (bittiğinde)
    if (primaryVideoUrl) previewMedia.push({ url: primaryVideoUrl, kind: "video" });
  } else if (
    ["tts-avatar", "image-to-avatar", "image_to_avatar_generation", "tavus-video"].includes(
      job.type
    )
  ) {
    // A user-uploaded avatar's preview can be a host-less /api/… path.
    const avatarImg = withMwRoot(metadata.avatarImageUrl || job.sourceUrl);
    if (avatarImg) previewMedia.push({ url: avatarImg, kind: "image" });
    if (primaryVideoUrl) previewMedia.push({ url: primaryVideoUrl, kind: "video" });
  } else {
    // Default: source/result media
    // Tek ses kaynağı seç: önce resultUrl (ses ise), yoksa SADECE seslendirmede
    // result/metadata audioUrl. Böylece başka job tipleri metadata.audioUrl içerse bile etkilenmez.
    // Ayrıca metadata'da kalan eski/silinmiş ses, yeni sesin altına liste olarak eklenmez.
    const isTts =
      (job.type || "")
        .toLowerCase()
        .trim()
        .replace(/[\s_]+/g, "-") === "text-to-speech";
    const audioUrl =
      (isAudioFile(job.resultUrl) ? job.resultUrl : "") ||
      (isTts ? videoData.audioUrl || metadata.audioUrl || "" : "") ||
      "";
    // text-to-image: görsel resultUrl yerine generatedImages / result.images içinde gelir
    const isT2i =
      (job.type || "")
        .toLowerCase()
        .trim()
        .replace(/[\s_]+/g, "-") === "text-to-image";
    const firstImg = videoData.images?.[0];
    const t2iImage = isT2i
      ? (metadata as any).generatedImages?.[0]?.url ||
        (typeof firstImg === "string" ? firstImg : firstImg?.url) ||
        videoData.imageUrl ||
        (metadata as any).imageUrl ||
        ""
      : "";
    // İnfografik sahne (infographic-scene-generate / infographic-images-generate): üretilen görseller metadata'da liste halinde gelir
    const normInfographicType = (job.type || "")
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-");
    const isInfographic =
      normInfographicType === "infographic-scene-generate" ||
      normInfographicType === "infographic-images-generate" ||
      normInfographicType === "infographic-image-generate";
    // Kartta hepsini değil, sığacak kadar (max 3) göster
    const infographicImages: string[] = isInfographic
      ? (
          ((metadata as any).generatedImages?.map((g: any) => g?.url).filter(Boolean) ||
            (metadata as any).images ||
            []) as string[]
        ).slice(0, 3)
      : [];
    // Text Extract (extract-text): kartta sadece kaynak (önceki) görseli göster
    const isTextExtract =
      ((job as any).operation || job.metadata?.operation || "").toString().toLowerCase() ===
        "extract-text" || (job.name || "").toLowerCase().replace(/[\s-]+/g, "_") === "extract_text";
    const sourceUrl = withMwRoot(job.sourceUrl);
    const candidates = (
      isTextExtract
        ? [sourceUrl]
        : [sourceUrl, job.resultUrl, primaryVideoUrl, t2iImage, ...infographicImages]
    ).filter(Boolean) as string[];
    candidates.forEach((url) => {
      if (isAudioFile(url)) return; // ses ayrı ele alınıyor (tek oynatıcı)
      const isVideo = url.match(/\.(mp4|webm|ogg|mov)(\?|#|$)/i);
      previewMedia.push({ url, kind: isVideo ? "video" : "image" });
    });
    if (audioUrl) previewMedia.push({ url: audioUrl, kind: "audio" });
  }

  // Deduplicate by URL to avoid double rendering
  const uniqueMedia: { url: string; kind: "video" | "image" | "audio" }[] = [];
  const seen = new Set<string>();
  for (const media of previewMedia) {
    if (seen.has(media.url)) continue;
    seen.add(media.url);
    uniqueMedia.push(media);
  }

  // Ses (text-to-speech) çıktısını görsel/video önizlemesinden ayır:
  // küçük ikon yerine kartta doğrudan dinlenebilir bir oynatıcı göster.
  const visualMedia = isNarration ? [] : uniqueMedia.filter((m) => m.kind !== "audio");
  const audioMedia = isNarration ? [] : uniqueMedia.filter((m) => m.kind === "audio");

  return (
    <div className="relative group">
      <button
        onClick={onClick}
        className="w-full text-left flex flex-col gap-3 p-4 rounded-2xl bg-white hover:bg-gray-50 active:scale-[0.98] transition-all duration-150 ease-out border border-gray-200"
      >
        {/* Top row: icon + info + status badge */}
        <div className="flex items-start gap-3">
          {/* Icon */}
          {isAvatarType ? (
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-blue-500">
                {/* Simple person icon */}
                <circle
                  cx="12"
                  cy="7"
                  r="4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="currentColor"
                  fillOpacity="0.2"
                />
                <path
                  d="M5 20v-1c0-3.3 2.7-6 6-6h2c3.3 0 6 2.7 6 6v1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          ) : job.type === "image-edit" ||
            job.type === "image_edit" ||
            job.type === "image-editing" ||
            job.type === "image_editing" ? (
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-blue-500">
                {/* Image frame */}
                <rect
                  x="2"
                  y="4"
                  width="20"
                  height="16"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="currentColor"
                  fillOpacity="0.08"
                />
                {/* Mountain/landscape in image */}
                <path
                  d="M2 16l5-5 4 4 5-6 6 7v4c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-4z"
                  fill="currentColor"
                  opacity="0.2"
                />
                <circle cx="7" cy="9" r="1.5" fill="currentColor" opacity="0.5" />
                {/* Edit pencil overlay */}
                <circle cx="18" cy="7" r="5" fill="white" stroke="currentColor" strokeWidth="1.2" />
                <path
                  d="M16 7l1.5 1.5M18.5 5.5l1.5 1.5-3 3-1.5-1.5 3-3z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          ) : job.type === "staged-video-init" || job.type === "staged_video_init" ? (
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-blue-500">
                {/* Storyboard: 2x2 panel grid */}
                <rect
                  x="2"
                  y="2"
                  width="9"
                  height="7"
                  rx="1.2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="currentColor"
                  fillOpacity="0.15"
                />
                <rect
                  x="13"
                  y="2"
                  width="9"
                  height="7"
                  rx="1.2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="currentColor"
                  fillOpacity="0.15"
                />
                <rect
                  x="2"
                  y="11"
                  width="9"
                  height="7"
                  rx="1.2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="currentColor"
                  fillOpacity="0.15"
                />
                <rect
                  x="13"
                  y="11"
                  width="9"
                  height="7"
                  rx="1.2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="currentColor"
                  fillOpacity="0.15"
                />
                <path d="M16 14l4 2.5-4 2.5V14z" fill="currentColor" opacity="0.8" />
                <line
                  x1="4"
                  y1="5.5"
                  x2="9"
                  y2="5.5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  opacity="0.6"
                />
                <line
                  x1="15"
                  y1="5.5"
                  x2="20"
                  y2="5.5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  opacity="0.6"
                />
                <line
                  x1="4"
                  y1="14.5"
                  x2="9"
                  y2="14.5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  opacity="0.6"
                />
              </svg>
            </div>
          ) : job.type === "staged-video-generation" || job.type === "staged_video_generation" ? (
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-blue-500">
                {/* Video frame */}
                <rect
                  x="2"
                  y="4"
                  width="20"
                  height="16"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="currentColor"
                  fillOpacity="0.1"
                />
                {/* Play button */}
                <path d="M9.5 8l6 4-6 4V8z" fill="currentColor" opacity="0.85" />
              </svg>
            </div>
          ) : job.type === "subtitle-generation" || job.type === "subtitle_generation" ? (
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-blue-500">
                {/* Video frame */}
                <rect
                  x="2"
                  y="3"
                  width="20"
                  height="14"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="currentColor"
                  fillOpacity="0.08"
                />
                {/* Subtitle bar at bottom */}
                <rect x="4" y="19" width="16" height="3" rx="1" fill="currentColor" opacity="0.3" />
                {/* CC text */}
                <text
                  x="12"
                  y="12"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="7"
                  fontWeight="bold"
                  fill="currentColor"
                  opacity="0.85"
                >
                  CC
                </text>
              </svg>
            </div>
          ) : job.type === "training-video-export" || job.type === "training_video_export" ? (
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-blue-500">
                {/* Video frame */}
                <rect
                  x="2"
                  y="4"
                  width="16"
                  height="12"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="currentColor"
                  fillOpacity="0.08"
                />
                {/* Play triangle */}
                <path d="M8 8l5 3-5 3V8z" fill="currentColor" opacity="0.6" />
                {/* Export arrow */}
                <path
                  d="M18 8v10M15 15l3 3 3-3"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          ) : job.type === "text-to-image" || job.type === "text_to_image" ? (
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-blue-500">
                {/* Image frame */}
                <rect
                  x="2"
                  y="4"
                  width="20"
                  height="16"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="currentColor"
                  fillOpacity="0.08"
                />
                <path
                  d="M2 16l5-5 4 4 5-6 6 7v2c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-2z"
                  fill="currentColor"
                  opacity="0.2"
                />
                <circle cx="8" cy="9" r="1.5" fill="currentColor" opacity="0.5" />
                {/* Sparkle (generation) */}
                <path
                  d="M18.5 2l.7 2.1 2.1.7-2.1.7-.7 2.1-.7-2.1L15.7 4.8l2.1-.7L18.5 2z"
                  fill="currentColor"
                  opacity="0.8"
                />
              </svg>
            </div>
          ) : job.type === "text-to-speech" ||
            job.type === "text_to_speech" ||
            job.type === "training-narration" ||
            job.type === "training_narration" ? (
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-blue-500">
                {/* Speaker */}
                <path
                  d="M4 9v6h4l5 4V5L8 9H4z"
                  fill="currentColor"
                  fillOpacity="0.25"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                {/* Sound waves */}
                <path
                  d="M16 8.5a4 4 0 010 7M18.5 6a7.5 7.5 0 010 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          ) : (
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-blue-500">
                <path
                  d="M12 3l1.5 4.5H18l-3.75 2.75L15.75 15 12 12.25 8.25 15l1.5-4.75L6 7.5h4.5L12 3z"
                  fill="currentColor"
                  opacity="0.7"
                />
                <path
                  d="M5 19h14M8 16l4 3 4-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          )}

          {/* Title + meta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {job.type === "staged-video-init" || job.type === "staged_video_init"
                  ? t("jobCard.storyboard")
                  : job.type === "staged-video-generation" || job.type === "staged_video_generation"
                    ? t("jobCard.sceneGeneration")
                    : jobDisplayName(job, t, i18n)}
              </div>
              {/* Status badge — flex, no absolute */}
              {getStatusBadge()}
            </div>
            {job.type === "training-narration" || job.type === "training_narration" ? (
              <div className="text-[12px] text-gray-500 mt-0.5 truncate">
                {t("jobCard.narrationDone", { count: Number(job.prompt?.match(/\d+/)?.[0] ?? 0) })}
              </div>
            ) : (
              (!isAvatarType || job.type === "lipsync" || isTalking) &&
              hasScene(job) &&
              !isQuestionBatch &&
              job.type !== "staged-video-init" &&
              job.type !== "staged_video_init" &&
              job.type !== "training-aspect-conversion" &&
              job.type !== "training_aspect_conversion" &&
              job.type !== "subtitle-generation" &&
              job.type !== "subtitle_generation" &&
              job.type !== "training-video-export" &&
              job.type !== "training_video_export" &&
              job.type !== "training-logo-overlay" &&
              job.type !== "training_logo_overlay" && (
                <div className="text-[12px] text-gray-500 mt-0.5 truncate">
                  {t("jobCard.scene")} {job.sceneIndex}
                </div>
              )
            )}
            {modelLabel && job.type !== "training-aspect-conversion" && (
              <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                {t("jobCard.model")}: {modelLabel}
              </div>
            )}
            {createdLabel && (
              <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                {t("jobCard.created")}: {createdLabel}
              </div>
            )}
          </div>
        </div>

        {/* Soru oluşturma: üretim sırasında ilerleme, bitince üretilen soru sayısı */}
        {isQuestionBatch && qb && (
          <div className="pl-[60px]">
            {qb.done ? (
              <p className="text-[12px] font-medium text-emerald-600">
                {t("questionBatch.generatedCount", { count: qb.questions.length || qb.total })}
              </p>
            ) : (
              <div className="space-y-1.5">
                <p className="text-[12px] text-gray-600">
                  {qb.total
                    ? t("questionBatch.generating", { total: qb.total, progress: qb.progress })
                    : t("questionBatch.generatingNoTotal", { progress: qb.progress })}
                </p>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all"
                    style={{ width: `${Math.min(100, Math.max(0, qb.progress))}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Doğal konuşan video: "Avatar video üretiliyor · %" + ilerleme */}
        {talkingRunning && (
          <div className="pl-[60px]">
            <div className="space-y-1.5">
              <p className="text-[12px] text-gray-600">
                {t("jobCard.talkingRunning", { progress: talkingProgress })}
              </p>
              <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all"
                  style={{ width: `${talkingProgress}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {(motionStep || motionApplied) && (
          <div className="pl-[60px]">
            <div className="space-y-1.5">
              <p className="text-[12px] text-gray-600">{motionStep || motionApplied}</p>
              {motionStep && (
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all"
                    style={{ width: `${talkingProgress}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Image / video preview strip */}
        {visualMedia.length > 0 && (
          <div className="flex gap-2 pl-[60px]">
            {visualMedia.map((media, i) => (
              <div
                key={i}
                className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center"
              >
                {media.kind === "video" ? (
                  <video
                    src={media.url}
                    className="w-full h-full object-cover"
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img src={media.url} className="w-full h-full object-cover" />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Audio player (text-to-speech) — kartta doğrudan dinle */}
        {audioMedia.length > 0 && (
          <div className="space-y-2 pl-[60px]">
            {audioMedia.map((media, i) => (
              <AudioPlayer key={i} src={media.url} />
            ))}
          </div>
        )}

        {/* Action buttons */}
        {(onApproveClick || onDeclineClick || onDismissClick) && (
          <div className="flex gap-2 justify-between items-center">
            {/* Dismiss button - left side */}
            {onDismissClick && !showHistory && (
              <button
                className="min-h-[44px] px-3 inline-flex items-center justify-center rounded-lg text-[12px] font-semibold text-gray-600 border border-gray-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300 active:scale-[0.98] transition-all duration-150 ease-out"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismissClick();
                }}
              >
                {t("actions.dismiss")}
              </button>
            )}

            {/* Approve/Decline buttons - right side */}
            <div className="flex gap-2 ml-auto">
              {onApproveClick && (
                <button
                  disabled={isApproving}
                  className="min-h-[44px] px-4 inline-flex items-center justify-center rounded-lg text-[12px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] transition-all duration-150 ease-out disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                  onClick={async (e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    if (isApproving) return;
                    setIsApproving(true);
                    const isVideoType = isVideoGeneration || isAvatarType;
                    const videoUrl =
                      primaryVideoUrl || job.result?.videoUrl || job.resultUrl || job.sourceUrl;

                    // talking-avatar is applied by the host's approve handler
                    // (narration swap + muted video) — adding the voiced video
                    // here first would put it on the scene twice.
                    if (isVideoType && videoUrl && !isTalking) {
                      onAddToScene({ jobId: job.id, videoUrl, hasVideo: true, navId: job.navId });
                    }

                    try {
                      await onApproveClick?.();
                    } finally {
                      setIsApproving(false);
                    }
                  }}
                >
                  {approveLabel || t("actions.approve")}
                </button>
              )}
              {onDeclineClick && (
                <button
                  className="min-h-[44px] px-3 inline-flex items-center justify-center rounded-lg text-[12px] font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 active:scale-[0.98] transition-all duration-150 ease-out"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeclineClick();
                  }}
                >
                  {declineLabel || t("actions.decline")}
                </button>
              )}
            </div>
          </div>
        )}
      </button>
    </div>
  );
}
